import type { UserStakingStatsResponse } from "@solomon-apps/shared";
import { API_URLS, DEFAULT_QUERY_OPTIONS } from "@solomon-apps/shared";
import { useQuery } from "@tanstack/react-query";
import type { UseQueryResult } from "@tanstack/react-query";

/**
 * Fetch user-specific staking statistics from API
 * Returns null if user has no staking account (404)
 */
async function fetchUserStakingStats(address: string, signal?: AbortSignal): Promise<UserStakingStatsResponse | null> {
    const url = `${API_URLS.SOLOMON_DATA}/api/solomon-protocol/staking/accounts/${address}/stats`;
    const response = await fetch(url, { signal });

    // User hasn't staked yet - return null instead of throwing
    if (response.status === 404) {
        return null;
    }

    if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
    }

    return response.json() as Promise<UserStakingStatsResponse>;
}

/**
 * Hook to fetch user-specific staking statistics
 *
 * Returns:
 * - stakedAmount: User's staked USDv amount
 * - shares: User's sUSDv shares
 * - yieldStats.totalYield: Total yield earned (all-time)
 * - unstakingQueue: Pending unstaking requests
 *
 * @param address - User's wallet address (null to skip fetching)
 *
 * @example
 * ```tsx
 * const { publicKey } = useWallet();
 * const { data, isLoading } = useUserStakingStats(publicKey);
 *
 * if (data?.yieldStats?.totalYield) {
 *   console.log(`Total yield: ${data.yieldStats.totalYield}`);
 * }
 * ```
 */
export function useUserStakingStats(address: string | null): UseQueryResult<UserStakingStatsResponse | null> {
    return useQuery<UserStakingStatsResponse | null>({
        queryKey: ["user-staking-stats", address],
        queryFn: ({ signal }): Promise<UserStakingStatsResponse | null> => {
            if (!address) {
                return Promise.reject(new Error("No address provided"));
            }
            return fetchUserStakingStats(address, signal);
        },
        enabled: !!address,
        ...DEFAULT_QUERY_OPTIONS
    });
}
